import React, { useEffect, useMemo, useState } from 'react';
import { Box, Select, SelectProps, SpaceBetween } from '@amzn/awsui-components-core';
import { PropertyFilterQuery } from '@amzn/awsui-collection-hooks';
import constants from '../../common/constants';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import LocalStorage from '../../common/LocalStorage';
import { v4 as uuid } from 'uuid';
import { localLog } from '../../loggers/Logger';
import CustomFilterActionButtonDropdown from './CustomFilterActionButtonDropdown';
import { getCustomFilterListKey, getDefaultFilterKey } from './ContentWorkflowTableFilters';

interface Props {
  tableId: string;
  query: PropertyFilterQuery;
  onQueryChange?: (query: PropertyFilterQuery) => void;
}

const CustomFilterActionControls: React.FunctionComponent<Props> = (props: Props) => {
  const { query, tableId, onQueryChange } = props;

  const customFilterListKey = getCustomFilterListKey(tableId);
  const defaultFilterKey = getDefaultFilterKey(tableId);
  const MAX_CUSTOM_FILTERS = 25;

  const [filterList, setFilterList] = useState(new Array<SelectProps.Option>());
  const [filterDefinitions, setFilterDefinitions] = useState(new Map<string, any>());
  const [savedFilter, setSavedFilter] = useState('');
  const [deletedFilter, setDeletedFilter] = useState('');
  const [defaultFilter, setDefaultFilter] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<SelectProps.Option>();
  const flashbarMessageBroker: FlashbarMessageBroker = useMemo(
    () => FlashbarMessageBroker.create(),
    [],
  );

  useEffect(() => {
    try {
      const customFilterList = LocalStorage.getTableQueryFilter(customFilterListKey);
      const defaultFilterId = LocalStorage.getTableQueryFilter(defaultFilterKey);
      if (customFilterList && typeof customFilterList === 'string') {
        const cachedFilterList = JSON.parse(customFilterList);
        const cachedFilterDefinitions = new Map<string, any>();
        // @ts-expect-error TS7006 - strict migration
        cachedFilterList.forEach((filter) => {
          const cachedQuery = LocalStorage.getTableQueryFilter(`${tableId}:${filter.value}`);
          if (cachedQuery && typeof cachedQuery === 'string') {
            cachedFilterDefinitions.set(filter.value, JSON.parse(cachedQuery));
            if (
              defaultFilterId &&
              typeof defaultFilterId === 'string' &&
              filter.value === defaultFilterId
            ) {
              setSelectedFilter(filter);
            }
          }
        });
        setFilterList(cachedFilterList);
        setFilterDefinitions(cachedFilterDefinitions);
      }
    } catch (error) {
      localLog.warn({ error }, `Error while loading custom filters for ${tableId}`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (defaultFilter) {
      LocalStorage.setTableQueryFilter(defaultFilterKey, defaultFilter);
    }
  }, [defaultFilter, defaultFilterKey]);

  useEffect(() => {
    if (savedFilter) {
      filterDefinitions.set(savedFilter, query);
      LocalStorage.setTableQueryFilter(`${tableId}:${savedFilter}`, JSON.stringify(query));
    }
  }, [savedFilter, filterDefinitions, query, tableId]);

  useEffect(() => {
    if (deletedFilter) {
      filterDefinitions.delete(deletedFilter);
      LocalStorage.removeTableQueryFilter(`${tableId}:${deletedFilter}`);
    }
  }, [deletedFilter, filterDefinitions, tableId]);

  useEffect(() => {
    localLog.info(`filterList length: ${filterList.length}`);
    if (filterList?.length > 0) {
      LocalStorage.setTableQueryFilter(customFilterListKey, JSON.stringify(filterList));
    }
  }, [filterList, customFilterListKey]);

  const onFilterChanged = (filterOption: SelectProps.Option) => {
    if (filterOption && filterOption.value) {
      setSelectedFilter(filterOption);
      const filterQuery = filterDefinitions.get(filterOption.value);
      if (filterQuery && onQueryChange) {
        onQueryChange(filterQuery as PropertyFilterQuery);
      }
    }
  };

  // @ts-expect-error TS7030 - strict migration
  const validateFilterName = (filterName: string) => {
    if (!!filterList.find((filter) => filter.label === filterName)) {
      return 'Duplicate filter view name';
    }
  };

  const onValidateCreateNew = () => {
    if (filterList.length >= MAX_CUSTOM_FILTERS) {
      flashbarMessageBroker.pushErrorMessage(
        `Not allowed to create more than ${MAX_CUSTOM_FILTERS} custom filters`,
      );
      return false;
    }
    return true;
  };

  const onValidateDelete = () => {
    if (filterList.length <= 1) {
      flashbarMessageBroker.pushErrorMessage(
        'Not allowed to delete all filters. At least one filter view needs to be present',
      );
      return false;
    }
    return true;
  };

  const onValidateRename = () => {
    if (!selectedFilter?.value) {
      flashbarMessageBroker.pushErrorMessage('Select a filter first for rename');
      return false;
    }
    return true;
  };

  const onSetDefaultFilter = () => {
    if (selectedFilter?.value) {
      setDefaultFilter(selectedFilter?.value);
      flashbarMessageBroker.pushSuccessMessage(constants.SEARCH_FILTERS_SET_DEFAULT_SUCCESS_TEXT);
    }
  };

  const onSaveFilter = () => {
    if (selectedFilter?.value) {
      setSavedFilter(selectedFilter?.value);
      flashbarMessageBroker.pushSuccessMessage(constants.SEARCH_FILTERS_SAVE_SUCCESS_TEXT);
    }
  };

  // @ts-expect-error TS7030 - strict migration
  const onSaveAsFilter = (filterName: string, isDefault?: boolean) => {
    if (filterName) {
      const validationError = validateFilterName(filterName);
      if (!!validationError) {
        return validationError;
      }
      const newFilter = { label: filterName, value: uuid() };
      const updatedFilterList = filterList.concat([newFilter]);
      setFilterList(updatedFilterList);
      setSavedFilter(newFilter.value);
      setSelectedFilter(newFilter);
      if (isDefault) {
        setDefaultFilter(newFilter.value);
      }
      flashbarMessageBroker.pushSuccessMessage(constants.SEARCH_FILTERS_SAVE_SUCCESS_TEXT);
    }
  };

  const onDeleteFilter = () => {
    if (selectedFilter?.value) {
      const updatedFilterList = filterList.filter(
        (filter) => filter.value !== selectedFilter?.value,
      );
      setFilterList(updatedFilterList);
      setDeletedFilter(selectedFilter.value);
      // @ts-expect-error TS2345 - strict migration
      setSelectedFilter(null);
      flashbarMessageBroker.pushSuccessMessage(constants.SEARCH_FILTERS_DELETE_SUCCESS_TEXT);
    }
  };

  // @ts-expect-error TS7030 - strict migration
  const onRenameFilter = (filterName: string) => {
    if (selectedFilter?.value && filterName) {
      const validationError = validateFilterName(filterName);
      if (!!validationError) {
        return validationError;
      }
      const updatedFilterList = filterList.filter(
        (filter) => filter.value !== selectedFilter?.value,
      );
      const renamedFilter = { label: filterName, value: selectedFilter?.value };
      updatedFilterList.push(renamedFilter);
      setFilterList(updatedFilterList);
      setSelectedFilter(renamedFilter);
      flashbarMessageBroker.pushSuccessMessage(constants.SEARCH_FILTERS_RENAME_SUCCESS_TEXT);
    }
  };

  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Box display="inline-block">
        <Select
          // @ts-expect-error TS2322 - strict migration
          selectedOption={selectedFilter}
          onChange={({ detail }) => onFilterChanged(detail.selectedOption)}
          options={filterList}
          placeholder={'Choose filter view'}
          empty={'No options'}
          ariaRequired
        />
      </Box>
      <Box display="inline-block">
        <CustomFilterActionButtonDropdown
          onDeleteFilter={onDeleteFilter}
          // @ts-expect-error TS2322 - strict migration
          onRenameFilter={onRenameFilter}
          onSaveFilter={onSaveFilter}
          // @ts-expect-error TS2322 - strict migration
          onSaveAsFilter={onSaveAsFilter}
          onSetDefaultFilter={onSetDefaultFilter}
          onValidateCreateNew={onValidateCreateNew}
          onValidateDelete={onValidateDelete}
          onValidateRename={onValidateRename}
          selectedFilter={selectedFilter}
        />
      </Box>
    </SpaceBetween>
  );
};

export default CustomFilterActionControls;
